import type { Role } from './auth/types';

const CHURCH_WIDE: Role[] = ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'];

export const ROLE_HOME: Record<Role, string> = {
  super_admin: '/admin',
  chief_pastor: '/admin',
  associate_pastor: '/admin',
  church_admin: '/admin',
  zonal_leader: '/admin/zonal',
  department_leader: '/admin/departments',
  unit_leader: '/admin/departments',
};

export const ROLE_ALLOWED_PREFIXES: Record<Role, string[]> = {
  super_admin: ['/admin', '/account'],
  chief_pastor: ['/admin', '/account'],
  associate_pastor: ['/admin', '/account'],
  church_admin: ['/admin', '/account'],
  zonal_leader: ['/admin', '/account'],
  department_leader: ['/admin', '/account'],
  unit_leader: ['/admin', '/account'],
};

export function roleHomePath(role: Role | null | undefined): string {
  if (!role) return '/login';
  return ROLE_HOME[role] ?? '/login';
}

export function canAccessPath(role: Role | null | undefined, pathname: string): boolean {
  if (!role) return false;
  const prefixes = ROLE_ALLOWED_PREFIXES[role] ?? [];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function hasRole(userRole: Role | null | undefined, allowed: Role | Role[]): boolean {
  if (!userRole) return false;
  const list = Array.isArray(allowed) ? allowed : [allowed];
  return list.includes(userRole);
}

export function isChurchWide(role: Role | null | undefined): boolean {
  return !!role && CHURCH_WIDE.includes(role);
}

export type Action =
  | 'admin:users'
  | 'admin:orgs'
  | 'dashboard:read'
  | 'members:manage'
  | 'zonal:manage'
  | 'departments:manage'
  | 'events:read'
  | 'events:manage'
  | 'assets:manage'
  | 'communications:send'
  | 'communications:manage'
  | 'training:manage'
  | 'pastoral_care:manage'
  | 'expenses:submit'
  | 'expenses:approve'
  | 'expenses:manage'
  | 'hr:manage'
  | 'hr:payroll'
  | 'giving:read'
  | 'giving:manage'
  | 'giving:receipts';

const ACTION_ROLES: Record<Action, Role[]> = {
  'admin:users': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'admin:orgs': ['super_admin'],
  'dashboard:read': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
    'department_leader',
    'unit_leader',
  ],
  'members:manage': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
    'department_leader',
    'unit_leader',
  ],
  'zonal:manage': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
  ],
  'departments:manage': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'department_leader',
  ],
  'events:read': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
    'department_leader',
    'unit_leader',
  ],
  'events:manage': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
    'department_leader',
    'unit_leader',
  ],
  'assets:manage': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'communications:send': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
    'department_leader',
    'unit_leader',
  ],
  'communications:manage': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'training:manage': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
    'department_leader',
    'unit_leader',
  ],
  'pastoral_care:manage': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'expenses:submit': [
    'super_admin',
    'chief_pastor',
    'associate_pastor',
    'church_admin',
    'zonal_leader',
    'department_leader',
    'unit_leader',
  ],
  'expenses:approve': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'expenses:manage': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'hr:manage': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'hr:payroll': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'giving:read': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'giving:manage': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
  'giving:receipts': ['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin'],
};

export function canPerform(role: Role | null | undefined, action: Action): boolean {
  if (!role) return false;
  return (ACTION_ROLES[action] ?? []).includes(role);
}

export const ALL_PORTAL_ROLES: Role[] = [
  'super_admin',
  'chief_pastor',
  'associate_pastor',
  'church_admin',
  'zonal_leader',
  'department_leader',
  'unit_leader',
];
